/** TanStack Query wrappers for task CRUD operations. */

import { useCallback } from "react";
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PaginatedTaskList,
  TaskCreate,
  TaskResponse,
  TaskSummary,
  TaskUpdate,
} from "@patent-studio/api-types";
import {
  createTask,
  deleteTask,
  restoreTask,
  trashTask,
  getTask,
  listTasks,
  updateTask,
  type ListTasksParams,
} from "@/services/tasks";
import {
  CLIENTS_KEY,
  TASKS_KEY,
  generateReviewKey,
  taskDetailKey,
} from "./query-keys";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/** A stage of the workflow, as the server keeps the one a draft was last open at. */
type DraftStage = NonNullable<TaskUpdate["last_stage"]>;

/** `staleTime` overrides the app's window for a reader that must see the list as it is now. */
export function useTaskList(
  params: ListTasksParams = {},
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: [...TASKS_KEY, params],
    queryFn: () => listTasks(params),
    enabled,
    ...(staleTime === undefined ? {} : { staleTime }),
  });
}

export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: taskDetailKey(taskId ?? ""),
    queryFn: () => getTask(taskId as string),
    enabled: taskId !== null,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.draftCreate,
    mutationFn: (body: TaskCreate) => createTask(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
      // A new draft is one more on its client's card.
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useUpdateTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    // One task's updates run one at a time. Two PATCHes to the same document
    // in flight together can reach the server in either order, so the older
    // value can be applied last and win — which a debounced autosave produces
    // whenever a save outlives its debounce. Scoped per task rather than
    // globally, so a write to one draft never waits on a write to another.
    scope: { id: `task-update-${taskId}` },
    mutationFn: (body: TaskUpdate) => updateTask(taskId, body),
    onSuccess: (data: TaskResponse) => {
      qc.setQueryData(taskDetailKey(taskId), data);
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

/** Set the invention's title the draft prints; the draft's name (its working title) stays as it is. */
export function useUpdateInventionTitle() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.inventionTitle,
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) =>
      updateTask(taskId, { invention_title: title }),
    onSuccess: (data: TaskResponse) => {
      qc.setQueryData(taskDetailKey(data.task_id), data);
      void qc.invalidateQueries({ queryKey: generateReviewKey(data.task_id) });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.draftDelete,
    mutationFn: (taskId: string) => deleteTask(taskId),
    onSuccess: (_data, taskId) => {
      qc.removeQueries({ queryKey: taskDetailKey(taskId) });
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

/** Whether a cached read under the tasks is one of the draft lists, rather than a draft or its parts. */
function isDraftList(query: Query): boolean {
  return typeof query.queryKey[1] === "object";
}

/**
 * A move into or out of Recently deleted: it changes which list a draft is in
 * and its client's counts, and nothing a draft's own reads hold.
 */
function useBinMove(
  mutationKey: readonly string[],
  move: (taskId: string) => Promise<TaskSummary>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: (taskId: string) => move(taskId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: TASKS_KEY,
        predicate: isDraftList,
      });
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

/** Moves a draft to Recently deleted. */
export function useTrashTask() {
  return useBinMove(MUTATION_KEYS.draftTrash, trashTask);
}

/** Returns a draft from Recently deleted. */
export function useRestoreTask() {
  return useBinMove(MUTATION_KEYS.draftRestore, restoreTask);
}

/**
 * Keeps on a draft the stage it is open at. Once kept, every cached draft list
 * holding the draft is given the stage without a read, since the lists are
 * trusted for a while and a hub opened from one would otherwise send the
 * attorney back to the stage before. In the same scope as the draft's other
 * updates, so writes to one draft land in the order they were made.
 */
export function useRecordStage(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.draftStage,
    scope: { id: `task-update-${taskId}` },
    mutationFn: (stage: DraftStage) =>
      updateTask(taskId, { last_stage: stage }),
    onSuccess: (_data, stage) => {
      qc.setQueriesData<PaginatedTaskList>(
        {
          queryKey: TASKS_KEY,
          // Not a list already due to be read again: writing into it would pass it off as
          // fresh, and a draft made since would stay missing until it went stale by the clock.
          // Its next read brings the stage anyway.
          predicate: (query) =>
            isDraftList(query) && !query.state.isInvalidated,
        },
        (list) =>
          list && {
            ...list,
            items: list.items.map((item) =>
              item.task_id === taskId ? { ...item, last_stage: stage } : item,
            ),
          },
      );
    },
  });
}

/**
 * Bring everything read off a draft level with a write to it.
 *
 * One invalidation of the tasks prefix: the draft list, the task, and every
 * query nested under the task's key (figures, positions, the invention) all
 * match it, so a removed drawing loses its card and a landed upload gains its
 * row without any of them being named here. That nesting is load-bearing:
 * flatten those keys and a stale card stays, with nothing failing to say so.
 */
export function useTaskRefresh(taskId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(async () => {
    if (!taskId) return;
    await qc.invalidateQueries({ queryKey: TASKS_KEY });
  }, [qc, taskId]);
}
